// FROM: https://github.com/modusign/modusign-client/blob/b6c3d577fdd8384ea2f73c5ab605ead1afc782d1/app/src/components/FieldLocatorSignature/SignatureFieldDropdownButton/index.tsx

import { Box, Flex, Text, Tooltip } from '@modusign/design-system';
import { SignatureType } from '@modusign/react-signing/types';
import { useMemo } from 'react';

import { ArrowButton } from '@app/components/Buttons';
import FieldLocatorBadge from '@app/components/FieldLocatorBadge';
import SignatureFieldMarkerIcon from '@app/components/SignatureFieldMarkerIcon';
import orderRotationForColor from '@app/libs/participantFields/orderRotationForColor';
import AllowSignatureType from '@app/store/AllowSignatureType';

interface SignatureFieldDropdownButtonProps {
  allowSignatureTypes: SignatureType[];
  count: number;
  order: number;
  onClick: () => void;
  onToggle: () => void;
}

function SignatureFieldDropdownButton({
  allowSignatureTypes,
  count,
  order,
  onClick,
  onToggle,
}: SignatureFieldDropdownButtonProps) {
  const markerIconType = useMemo(() => {
    if (
      allowSignatureTypes.includes(AllowSignatureType.SIGN) &&
      allowSignatureTypes.includes(AllowSignatureType.STAMP)
    ) {
      return 'CIRCLE';
    }

    if (allowSignatureTypes.includes(AllowSignatureType.SIGN)) {
      return 'SIGN';
    }

    if (allowSignatureTypes.includes(AllowSignatureType.STAMP)) {
      return 'STAMP';
    }

    return 'CIRCLE';
  }, [allowSignatureTypes]);

  const signatureTypeToolTipMessage = useMemo(() => {
    if (
      allowSignatureTypes.includes(AllowSignatureType.SIGN) &&
      allowSignatureTypes.includes(AllowSignatureType.STAMP)
    ) {
      return (
        <Text>
          사인 또는 도장 중, 서명자가 원하는
          <br />
          방식으로 입력하도록 합니다.
        </Text>
      );
    }

    if (allowSignatureTypes.includes(AllowSignatureType.SIGN)) {
      return '사인으로 서명을 입력하도록 합니다.';
    }

    if (allowSignatureTypes.includes(AllowSignatureType.STAMP)) {
      return '도장으로 서명을 입력하도록 합니다.';
    }

    return (
      <Text>
        사인 또는 도장 중, 서명자가 원하는
        <br />
        방식으로 입력하도록 합니다.
      </Text>
    );
  }, [allowSignatureTypes]);

  const buttonText = useMemo(() => {
    if (
      allowSignatureTypes.includes(AllowSignatureType.SIGN) &&
      allowSignatureTypes.includes(AllowSignatureType.STAMP)
    ) {
      return '사인·도장';
    }

    if (allowSignatureTypes.includes(AllowSignatureType.SIGN)) {
      return '사인';
    }

    if (allowSignatureTypes.includes(AllowSignatureType.STAMP)) {
      return '도장';
    }

    return '사인·도장';
  }, [allowSignatureTypes]);

  const handleClick = (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onClick();
  };

  const colorByOrder = `participantOrder${orderRotationForColor(order)}`;

  return (
    <Tooltip content={<Text>{signatureTypeToolTipMessage}</Text>} placement="right">
      <Flex
        bg="white"
        border="1px solid"
        borderColor="lightGray300"
        borderRadius="4px"
        height="36px"
        mb={2}
        overflow="hidden"
        position="relative"
        width="100%"
      >
        <Flex
          alignItems="center"
          className="SignatureFieldDropdownButton__button"
          justifyContent="flex-start"
          sx={{
            cursor: 'pointer',
            outline: 'none',
            '&:hover': {
              bg: 'lightGray50',
            },
          }}
          width="calc(100% - 35px)"
        >
          <Box
            alignItems="center"
            as="button"
            display="flex"
            justifyContent="space-between"
            pl="11px"
            pr={3}
            tabIndex={0}
            textAlign="left"
            type="button"
            width="100%"
            onClick={handleClick}
          >
            <Box width="100%">
              <Box
                display="flex"
                flexDirection="column"
                height="20px"
                justifyContent="center"
                mr={3}
                sx={{
                  float: 'left',
                }}
                textAlign="center"
                width="20px"
              >
                <Box height="18px">
                  <SignatureFieldMarkerIcon color={colorByOrder} size={18} type={markerIconType} />
                </Box>
              </Box>
              <Text as="span" color="textNeutralLight" fontSize="md" fontWeight="medium">
                {buttonText}
              </Text>
            </Box>
            {count > 0 && <FieldLocatorBadge>{count}</FieldLocatorBadge>}
          </Box>
        </Flex>
        <ArrowButton
          aria-label="field-locator-signature-dropdown"
          direction="bottom"
          sx={{
            border: 'none',
            borderLeft: '1px solid',
            borderLeftColor: 'lightGray300',
            height: '100%',
            margin: 0,
            padding: 0,
            position: 'absolute',
            borderRadius: '0 3px 3px 0',
            right: 0,
            '&:hover': {
              bg: 'lightGray50',
            },
          }}
          onClick={() => {
            onToggle();
          }}
        />
      </Flex>
    </Tooltip>
  );
}

export default SignatureFieldDropdownButton;
